import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGamification } from './hooks/GamificationContext';

const RadioContext = createContext();

export const useRadio = () => useContext(RadioContext);

const FALLBACK_RADIO_SONGS = [
  { title: 'Flaschenjagd bei Nacht', src: '/wp-content/uploads/Pfandpiraten-Flaschenjagd-bei-Nacht.mp3', minLevel: 0 },
  { title: 'Die Jagd nach dem Pfand', src: '/wp-content/uploads/Die-Jagd-nach-dem-Pfand.mp3', minLevel: 0 },
  { title: 'Auf Schatzsuche', src: '/wp-content/uploads/Pfandpiraten-%E2%80%93-Auf-Schatzsuche.mp3', minLevel: 0 },
  { title: 'Flaschenjäger', src: '/wp-content/uploads/Flaschenjaeger.mp3', minLevel: 0 },
  { title: 'Flaschen für die Freiheit', src: '/wp-content/uploads/Flaschen-fuer-die-Freiheit.mp3', minLevel: 0 },
  { title: 'Auf der Jagd nach dem Schatz', src: '/wp-content/uploads/Pfandpiraten-%E2%80%93-Auf-der-Jagd-nach-dem-Schatz.mp3', minLevel: 0 },
  { title: 'Der Pfandpiraten Flow', src: '/wp-content/uploads/Der-Pfandpiraten-Flow.mp3', minLevel: 0 },
  { title: 'Pfandpiraten auf der Jagd', src: '/wp-content/uploads/Pfandpiraten-auf-der-Jagd.mp3', minLevel: 0 },
  { title: 'Die Nacht der Pfandpiraten', src: '/wp-content/uploads/Die-Nacht-der-Pfandpiraten.mp3', minLevel: 0 },
  { title: 'Dosenpfand', src: '/wp-content/uploads/Dosenpfand.mp3', minLevel: 0 },
  { title: 'Dosenraub', src: '/wp-content/uploads/Dosenraub.mp3', minLevel: 0 },
  { title: 'Grüner Schatz', src: '/wp-content/uploads/Gruener-Schatz.mp3', minLevel: 0 },
  { title: 'Kleine Taten große Wirkung', src: '/wp-content/uploads/Kleine-Taten-grosse-Wirkung.mp3', minLevel: 0 },
  { title: 'Pfand für den Tag', src: '/wp-content/uploads/Pfand-fuer-den-Tag.mp3', minLevel: 0 },
  { title: 'Mehr als Müll', src: '/wp-content/uploads/Mehr-als-Muell.mp3', minLevel: 0 },
  { title: 'Der Weg des Pfandes', src: '/wp-content/uploads/Der-Weg-des-Pfandes.mp3', minLevel: 0 },
  { title: 'Pfandland', src: '/wp-content/uploads/Pfandland.mp3', minLevel: 0 },
  { title: 'Gemeinsam für den Pfand', src: '/wp-content/uploads/Gemeinsam-fuer-den-Pfand.mp3', minLevel: 0 },
  { title: 'Der Zauberautomat', src: '/wp-content/uploads/Der-Zauberautomat.mp3', minLevel: 0 },
  { title: 'Pfand ist Freiheit', src: '/wp-content/uploads/Pfand-ist-Freiheit.mp3', minLevel: 0 },
];

function normalizeTrack(raw, index) {
  if (!raw || typeof raw !== 'object') return null;

  const src = String(raw.src || raw.audio_url || raw.audioUrl || raw.url || '').trim();
  if (!src) return null;

  const minLevel = Number(raw.minLevel ?? raw.min_level ?? raw.level ?? 0);

  return {
    id: raw.id || src || index,
    title: String(raw.title?.rendered || raw.title || `Track ${index + 1}`).trim(),
    src,
    coverUrl: String(raw.coverUrl || raw.cover_url || raw.featured_image_url || '').trim(),
    minLevel: Number.isFinite(minLevel) ? Math.max(0, minLevel) : 0,
    source: raw,
  };
}

function normalizeTrackResponse(payload) {
  const rawTracks = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.tracks)
      ? payload.tracks
      : [];

  return rawTracks
    .map(normalizeTrack)
    .filter(Boolean);
}

function findNextUnlockedIndex(songs, currentIndex, direction) {
  const unlockedIndexes = songs
    .map((song, index) => (song.isUnlocked ? index : -1))
    .filter((index) => index >= 0);

  if (!unlockedIndexes.length) return 0;

  const currentPosition = unlockedIndexes.indexOf(currentIndex);
  if (currentPosition < 0) return unlockedIndexes[0];

  const basePosition = currentPosition;
  const nextPosition = (basePosition + direction + unlockedIndexes.length) % unlockedIndexes.length;

  return unlockedIndexes[nextPosition];
}

export const RadioProvider = ({ children }) => {
  const { level = 0 } = useGamification();
  const userLevel = Number(level) || 0;
  const [tracks, setTracks] = useState(FALLBACK_RADIO_SONGS);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [tracksError, setTracksError] = useState(null);
  const [isUsingFallbackTracks, setUsingFallbackTracks] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setMuted] = useState(false);

  useEffect(() => {
    const apiUrl = window?.pfandpiratData?.radioTracksApiUrl || '/wp-json/pfandpirat/v1/radio-tracks';
    let cancelled = false;

    async function loadTracks() {
      setTracksLoading(true);
      setTracksError(null);

      try {
        const response = await fetch(`${apiUrl}${apiUrl.includes('?') ? '&' : '?'}_pfp_ts=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'X-WP-Nonce': window?.pfandpiratData?.nonce || '',
          },
        });

        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(`Radio track request failed with status ${response.status}`);
        }

        const nextTracks = normalizeTrackResponse(payload);

        if (!cancelled && nextTracks.length) {
          setTracks(nextTracks);
          setUsingFallbackTracks(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Pfandpirat Radio Tracks konnten nicht geladen werden', error);
          setTracksError(error);
          setTracks(FALLBACK_RADIO_SONGS);
          setUsingFallbackTracks(true);
        }
      } finally {
        if (!cancelled) {
          setTracksLoading(false);
        }
      }
    }

    loadTracks();

    return () => {
      cancelled = true;
    };
  }, []);

  const songs = useMemo(
    () => tracks.map((track, index) => ({
      ...track,
      isUnlocked: userLevel >= Number(track.minLevel || 0),
      listIndex: index,
    })),
    [tracks, userLevel]
  );

  useEffect(() => {
    if (!songs.length) return;

    const current = songs[currentSongIndex];
    if (current?.isUnlocked) return;

    setCurrentSongIndex(findNextUnlockedIndex(songs, currentSongIndex, 1));
  }, [songs, currentSongIndex]);

  const togglePlay = useCallback(() => {
    setIsPlaying((previous) => !previous);
  }, []);

  const play = useCallback(() => setIsPlaying(true), []);
  const pause = useCallback(() => setIsPlaying(false), []);

  const goToPreviousSong = useCallback(() => {
    setCurrentSongIndex((index) => findNextUnlockedIndex(songs, index, -1));
  }, [songs]);

  const goToNextSong = useCallback(() => {
    setCurrentSongIndex((index) => findNextUnlockedIndex(songs, index, 1));
  }, [songs]);

  const selectSong = useCallback((index) => {
    if (!songs[index]?.isUnlocked) return false;

    setCurrentSongIndex(index);
    setIsPlaying(true);
    return true;
  }, [songs]);

  const toggleMute = useCallback(() => setMuted((muted) => !muted), []);

  const updateVolume = useCallback((nextVolume) => {
    setVolume(Math.max(0, Math.min(1, Number(nextVolume) || 0)));
    setMuted(false);
  }, []);

  const currentSong = songs[currentSongIndex] || songs.find((song) => song.isUnlocked) || songs[0] || FALLBACK_RADIO_SONGS[0];

  const value = useMemo(
    () => ({
      songs,
      currentSong,
      currentSongIndex,
      userLevel,
      tracksLoading,
      tracksError,
      isUsingFallbackTracks,
      isPlaying,
      volume,
      isMuted,
      togglePlay,
      play,
      pause,
      goToPreviousSong,
      goToNextSong,
      selectSong,
      toggleMute,
      updateVolume,
    }),
    [
      songs,
      currentSong,
      currentSongIndex,
      userLevel,
      tracksLoading,
      tracksError,
      isUsingFallbackTracks,
      isPlaying,
      volume,
      isMuted,
      togglePlay,
      play,
      pause,
      goToPreviousSong,
      goToNextSong,
      selectSong,
      toggleMute,
      updateVolume,
    ]
  );

  return (
    <RadioContext.Provider value={value}>
      {children}
    </RadioContext.Provider>
  );
};
